import { buildReportScopePromptSection } from '@process-advisor/process-miner/consultant/aiReportScope';
import { buildSharedConsultantPayload } from '@process-advisor/process-miner/consultant/consultantSharedAiPayload';

const normalizeSourceSystem = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'snow' || raw === 'servicenow' || raw === 'service_now') return 'servicenow';
  if (raw === 'forge' || raw === 'jira' || raw === 'atlassian' || raw === 'jira service management' || raw === 'jsm') return 'jira';
  return 'generic';
};

const buildPlatformContext = (args = {}) => {
  const sourceSystem = normalizeSourceSystem(args.sourceSystem || args.appVersion || args.AppVersion);

  if (sourceSystem === 'servicenow') {
    return {
      sourceSystem,
      platformName: 'ServiceNow',
      aiPlatformName: 'ServiceNow AI / Now Platform intelligence',
      readinessKey: 'servicenow_ai_readiness',
      readinessLabel: 'AI Readiness',
      opportunityHeading: 'AI, Automation & Optimisation Opportunities',
      productField: 'platform_product',
      productLabel: 'ServiceNow capability',
      productOptions: 'Now Assist | Predictive Intelligence | Virtual Agent | Flow Designer | Automation Engine | Assignment Rules | SLA/Performance Analytics | Process Change',
      productGuidance: `This is a ServiceNow workflow. The AI and automation platform is ServiceNow / Now Platform. Recommendations must use ServiceNow-native capabilities, not Atlassian Rovo or Jira Automation:

  Now Assist — summarisation, resolution notes, knowledge assistance, incident/request/change assistance, and work-note drafting inside ServiceNow.

  Predictive Intelligence — classification, assignment, similarity, clustering, routing, and prediction models based on ServiceNow task history.

  Virtual Agent — guided conversational intake, deflection, status updates, and request fulfilment.

  Flow Designer — low-code workflow automation for routing, approvals, notifications, fulfilment, and handoffs.

  Automation Engine / IntegrationHub — cross-system actions, integration-driven orchestration, and external enrichment.

  Assignment Rules / Data Lookup / Decision Tables — deterministic routing, priority, ownership, and classification logic.

  SLA and Performance Analytics — breach prediction, service performance monitoring, management reporting, and task_sla-derived SLA signals including breached count, completed SLA counts, business elapsed time, SLA name, and SLA stage.`,
      namingRules: `- Never mention Atlassian Rovo, Rovo Agents, Rovo Search, Rovo Chat, Rovo Studio, Jira Automation, JMWE, JWT, or ScriptRunner.
- Never say "Jira issue". Use ServiceNow wording such as task, incident, request, change, assignment group, state, SLA, business rule, Flow Designer, and Now Assist.
- Every recommendation must name the specific ServiceNow capability that delivers it.`,
      configNoteRule: '- platform_config_note: one concrete sentence on how to configure this in ServiceNow (Flow Designer, Predictive Intelligence, Now Assist, Assignment Rules, Business Rules, SLA/PA — be specific)',
      phaseRules: `- Phase 1 should be quick wins achievable with Now Assist, Performance Analytics review, or simple Flow Designer / Assignment Rule changes.
- Phase 2 should cover Predictive Intelligence, Virtual Agent, or Flow Designer automations for the highest-priority opportunities.
- Later phases address IntegrationHub, Automation Engine, custom apps, or cross-table orchestration.`,
      workflowAiRule: '- Mark ai_decision_point: true where a ServiceNow capability acts at that state, and name which capability and what it does in platform_action.',
    };
  }

  if (sourceSystem === 'generic') {
    return {
      sourceSystem,
      platformName: 'the source workflow platform',
      aiPlatformName: 'appropriate AI and automation capabilities',
      readinessKey: 'ai_readiness',
      readinessLabel: 'AI Readiness',
      opportunityHeading: 'AI, Automation & Optimisation Opportunities',
      productField: 'platform_capability',
      productLabel: 'Recommended capability',
      productOptions: 'Deterministic Automation | AI Assistance | Classification / Routing | Knowledge / Search | Integration / Orchestration | Process Change',
      productGuidance: `The source platform is not definitively identified. Keep recommendations platform-neutral. Recommend the capability pattern that is required rather than inventing a vendor product. Distinguish deterministic automation, AI assistance, classification/routing, knowledge/search, integration/orchestration, and pure process change.`,
      namingRules: `- Do not assume Jira, Atlassian Rovo, ServiceNow, Now Assist, or any other vendor unless the supplied context explicitly identifies it.
- Use the supplied case/entity and activity terminology.
- State the capability needed and the evidence supporting it.
- Prefer deterministic rules where the decision can be expressed reliably without AI.`,
      configNoteRule: '- platform_config_note: describe the configuration pattern required without inventing vendor-specific menus or products',
      phaseRules: `- Phase 1 should prioritise process simplification, data quality, and low-risk deterministic automation.
- Phase 2 may introduce classification, routing, knowledge assistance, or AI where evidence supports it.
- Later phases may include integration/orchestration or more advanced AI.`,
      workflowAiRule: '- Mark ai_decision_point: true only where an AI capability genuinely adds value; describe the required capability in platform_action.',
    };
  }

  return {
    sourceSystem,
    platformName: 'Jira / Jira Service Management',
    aiPlatformName: 'Atlassian Rovo',
    readinessKey: 'rovo_readiness',
    readinessLabel: 'Rovo Readiness',
    opportunityHeading: 'AI, Automation & Optimisation Opportunities',
    productField: 'rovo_product',
    productLabel: 'Rovo product',
    productOptions: 'Rovo Agents | Rovo Search | Rovo Chat | Rovo Studio | Jira Automation | Process Change',
    productGuidance: `This is a Jira Cloud / Jira Service Management workflow. Use the most appropriate Atlassian or process capability for each opportunity. Use Rovo only where AI is genuinely appropriate:

  Rovo Agents — autonomous agents that act inside Jira (route, close, label, escalate).
    Trigger via: Jira Automation rules or webhooks. Built in Rovo Studio for custom logic.

  Rovo Search — AI-powered search across Jira, Confluence, and connected tools.
    Surfaces similar past tickets, knowledge articles, and resolutions at point-of-work.
    No custom setup — available with Rovo enabled.

  Rovo Chat — conversational AI embedded in Jira tickets.
    Helps agents summarise threads, draft resolutions, find related issues, suggest next steps.
    Available in Jira Cloud with Rovo enabled — no custom build required.

  Rovo Studio — low-code builder for purpose-built Rovo Agents with custom logic.
    Use when standard Rovo Agents are not flexible enough.
    Requires Rovo premium; agents are versioned and testable.

  Jira Automation — deterministic rules for routing, status changes, reminders, field updates, approvals, and scheduled actions.
    Use this instead of AI when the trigger and action can be expressed reliably as rules.

  Process Change — workflow simplification, governance, status rationalisation, policy, role, or operating-model changes that do not require new automation or AI.`,
    namingRules: `- Never say "an AI model" — always name the specific Rovo product.
- Use Jira/Jira Service Management wording such as issue, ticket, request, project, status, assignee, queue, SLA, Jira Automation, and Rovo.
- Every AI recommendation must name the specific Rovo product that delivers it. Deterministic automation must use Jira Automation. Pure optimisation must use Process Change.
- Do not recommend Rovo merely because AI is available. Explicitly state when AI is not required and why.`,
    configNoteRule: '- rovo_config_note: one concrete sentence on how to configure this in Atlassian (Jira Automation rule, Rovo Studio agent, native Rovo Chat — be specific)',
    phaseRules: `- Phase 1 should prioritise low-effort process changes and deterministic Jira Automation where these address the highest-value evidence-backed problems.
- Phase 2 should apply Rovo only to opportunities with High or Medium AI suitability and a clear evidence-backed AI use case.
- Later phases may address more complex Rovo Studio agents or cross-tool integrations where justified by value and readiness.`,
    workflowAiRule: '- Mark ai_decision_point: true where a Rovo product acts at that state, and name which product and what it does in platform_action.',
  };
};

export const buildAIIntelligencePrompt = (args = {}) => {
  const platform = buildPlatformContext(args);
  const inputPayload =
    args.inputPayload ||
    buildSharedConsultantPayload({
      issueTypeStr:       args.issueTypeStr,
      metricsHistory:     args.metricsHistory,
      metricsTotal:       args.metricsTotal,
      transitionsJSON:    args.transitionsJSON,
      variantsJSON:       args.variantsJSON,
      sampleItems:        args.sampleItems,
      fieldUsage:         args.fieldUsage,
      statusTypeSummary:  args.statusTypeSummary,
      statusTypeMappings: args.statusTypeMappings,
      activityType:       args.activityType,
      activityContext:    args.activityContext,
      sourceSystem:       platform.sourceSystem,
      appVersion:         args.appVersion,
    });

  inputPayload.source_system = platform.sourceSystem;
  inputPayload.platform_name = platform.platformName;
  inputPayload.ai_platform_name = platform.aiPlatformName;

  const inputText = JSON.stringify(inputPayload, null, 2);
  const reportScopeText = buildReportScopePromptSection(args.reportScopeSummary);

  return `You are an expert ${platform.platformName} AI consultant producing the AI, Automation & Optimisation Opportunity assessment for a workflow.

PURPOSE
Produce a single unified opportunity assessment covering: what should be optimised, what should be automated, where AI should be applied, which ${platform.productLabel} or process change delivers each opportunity, how to sequence implementation, and what the workflow looks like once the recommendations are in place.

PLATFORM CONTEXT
${platform.productGuidance}
CANONICAL EVIDENCE RULES
- The JSON evidence payload is the same canonical Advisor evidence payload used across all Advisor AI content. Use it as the single source of truth.
- workload_signals.attribute_usage is the authoritative attribute/workload segmentation signal. Derive sparse/highly-used/classification conclusions from it rather than expecting separate duplicated sections.
- sample_evidence.rows contains random/scoped sample work items after report-scope and privacy rules. Use sample rows only when item-level examples, validation, or data-quality checks are necessary. Do not let sample rows override aggregate process metrics, variants, transitions, SLA signals, or attribute usage.

CONTROLLED ACTIVITY TYPE RULES
- The payload contains activity_context.analysis_type / activity_context.activity_kind. Treat this as the controlling interpretation, not merely a hint.
- Only use lifecycle/status evidence such as queue time, wait time, touch time, admin time, flow efficiency, status bottlenecks, or status progression when activity_kind is lifecycle_status.
- If activity_kind is routing_group, describe elapsed time as resolver-group dwell time, routing delay, escalation delay, handoff delay, or ownership dwell — not literal waiting in queues.
- If activity_kind is ownership, describe elapsed time as assignee dwell time, reassignment delay, ownership churn, or workload concentration — not status queue time.
- If activity_kind is classification, interpret transitions as classification changes, escalation/de-escalation, or data-quality movement. Do not describe this as workflow progress.
- If activity_kind is approval_state, interpret transitions as approval movement, approval dwell, rejection/rework, or governance delay.
- Respect activity_context.invalid_interpretations and activity_context.valid_metric_families when selecting evidence and wording.

GENERAL RULES
- source_system is ${platform.sourceSystem}. Do not mix platforms.
${platform.namingRules}
- Every recommendation must connect to a specific named signal in the evidence (a transition, a rework rate, a field quality issue, a variant count, or task_sla-derived SLA signal — not a generic statement).
- Never give generic advice that could apply to any workflow. If it is not grounded in the evidence, do not include it.

EVIDENCE QUALITY RULES
- Fields populated at or near 100% are usually mandatory. The population rate of such a field is NOT evidence of data quality, classification signal, or AI readiness — never cite near-100% population as a positive signal.
- Population rates are evidence only when meaningfully below 100% (a data-quality gap, which is itself an automation or data-quality opportunity) or when comparing optional fields against each other.
- Base readiness and classification claims on discriminative signals: label distribution and cardinality from workload_signals.attribute_usage (classification strength), record volume relative to label count, variant concentration, repeated rework or reassignment loops (a misrouting signal), and quantified delays or bottlenecks.
- readiness_rationale must lead with the strongest quantified signal in the evidence (a delay, a variant count, a rework rate, a classification-strength score) — never with field population.
- Do NOT produce a summary, headline, or executive overview — the Process Health tab already covers the process picture. Start directly with the opportunities.
- Use plain text only. Return JSON only. No markdown fences.

OPPORTUNITY PORTFOLIO RULES
- Provide 5 to 8 opportunities, ordered by priority.
- Build a balanced portfolio across Optimisation, Automation and AI when the evidence supports those categories. Do not force a category where there is no evidence.
- The purpose is to decide the right intervention, not to maximise the number of AI recommendations.
- Prefer Optimisation when the root cause is process design, governance, status proliferation, unclear ownership, or policy.
- Prefer Automation when the work is deterministic and rule-based.
- Prefer AI when the decision depends on unstructured content, prediction, similarity, classification, summarisation, knowledge retrieval, or judgement at scale.
- Explicitly distinguish AI from deterministic automation. An opportunity should have one primary opportunity_type: AI | Automation | Optimisation.
- Each opportunity card contains both the problem and the delivery mechanism — there is no separate product section.
- platform_product must be one of: ${platform.productOptions}. Use Process Change for pure optimisation opportunities.
- For Jira/Rovo output, also populate rovo_product with the same value as platform_product.
- ai_use_case: Triage | Classification | Prioritisation | Routing | Summarisation | Decision Support | Risk Detection | Knowledge Assist | Forecasting | Not Applicable. Use Not Applicable for non-AI opportunities.
- priority: Critical | High | Medium
- impact: Very High | High | Medium
- effort: Low | Medium | High
- confidence: High | Moderate | Low
- opportunity_type: AI | Automation | Optimisation.
- intervention_type: Optimize | Automate | Augment. Keep this legacy field consistent with opportunity_type: Optimisation = Optimize, Automation = Automate, AI = Augment. Classify every opportunity honestly — do not label deterministic automation as AI.
- recommended_treatment: Optimise | Automate | Apply AI. This is the plain-language action the customer should take and must align with opportunity_type.
- ai_suitability: High | Medium | Low | Not Required.
- ai_suitability_rationale: explain in one concise evidence-grounded sentence WHY AI is or is not the right intervention. For deterministic/rule-based work prefer Not Required or Low; for process/governance root causes prefer Not Required; for unstructured content, classification, prediction, similarity, summarisation, knowledge retrieval or judgement at scale use High/Medium when supported.
- recommended_platform_capability: the concrete Atlassian/process capability to use. For Jira this should normally be a specific Rovo product for AI, Jira Automation for deterministic automation, or Process Change for optimisation.
- value_potential: Very High | High | Medium | Low. Rate the likely business value using the evidence, not novelty.
- value_driver: Capacity | Cycle Time | Cost | Quality | Risk | Experience. Choose the primary value mechanism.
- value_hypothesis: one concise quantified or directional statement explaining how the opportunity creates value. If the evidence cannot support a number, state a directional outcome rather than inventing one.
- priority_score: integer 0-100 using this exact scoring rule: priority Critical=30 High=22 Medium=14; impact Very High=25 High=18 Medium=10; confidence High=15 Moderate=10 Low=5; effort Low=15 Medium=10 High=5; value_potential Very High=15 High=11 Medium=7 Low=3. Sum the five components.
- recommended_priority: Do Now | Plan | Investigate | Monitor. Normally map score >=75 to Do Now, >=60 to Plan, >=45 to Investigate, otherwise Monitor; you may only override the mapping when a concrete dependency/risk in the evidence requires it, and explain that in priority_rationale.
- priority_rationale: one concise sentence explaining the priority using value, effort, confidence, dependencies and urgency.
- time_horizon: 0-30 days | 30-60 days | 60-90 days | 90+ days.
- value_simulation_scenario: reassignment_churn | rework_loops | waiting_delay | approval_delay | variant_standardisation | top_bottleneck_activity | post_resolution_admin | best_path_shift | automate_repetitive_transition | none. Select the closest existing Value Simulation scenario; use none if no defensible mapping exists.
- platform_trigger: the specific workflow event that activates this (e.g. "Task created", "State changes to Resolved", "Ticket age exceeds 48 hours")
${platform.configNoteRule}
- Group into 2 to 4 themes.

IMPLEMENTATION SEQUENCE RULES
- Provide 3 to 4 phases.
${platform.phaseRules}
- Each phase must list specific named actions from the opportunities above.

TARGET WORKFLOW STATE RULES
- Show what the workflow looks like AFTER the recommended optimisation, automation and AI interventions are implemented — not a redesign exercise, but the natural outcome of applying the above.
- 4 to 6 states. Each state has: name, type (Open|Active|Waiting|Review|Closed), purpose, entry_rule, exit_rule.
${platform.workflowAiRule}
- preferred_flow: ordered array of state names showing the primary path.
- Keep it brief — this is a schematic, not a spec.

OUTPUT FORMAT
Return exactly one JSON object:

{
  "source_system": "${platform.sourceSystem}",
  "platform_name": "${platform.platformName}",
  "ai_platform_name": "${platform.aiPlatformName}",
  "readiness_label": "${platform.readinessLabel}",
  "opportunity_heading": "${platform.opportunityHeading}",
  "readiness": "High | Medium | Low",
  "readiness_rationale": "<1-2 sentences on automation/AI readiness, led by the strongest quantified signal — never by field population>",
  "rovo_readiness": "High | Medium | Low",
  "rovo_readiness_rationale": "<Jira only; for ServiceNow repeat readiness_rationale or leave empty>",

  "opportunities": [
    {
      "title": "<specific, named opportunity — not generic>",
      "opportunity_type": "AI | Automation | Optimisation",
      "ai_use_case": "Triage | Classification | Prioritisation | Routing | Summarisation | Decision Support | Risk Detection | Knowledge Assist | Forecasting | Not Applicable",
      "platform_product": "${platform.productOptions}",
      "rovo_product": "<Jira only: Rovo Agents | Rovo Search | Rovo Chat | Rovo Studio | Jira Automation | Process Change; ServiceNow: repeat platform_product>",
      "priority": "Critical | High | Medium",
      "impact": "Very High | High | Medium",
      "effort": "Low | Medium | High",
      "confidence": "High | Moderate | Low",
      "intervention_type": "Optimize | Automate | Augment",
      "recommended_treatment": "Optimise | Automate | Apply AI",
      "ai_suitability": "High | Medium | Low | Not Required",
      "ai_suitability_rationale": "<why AI is or is not appropriate, grounded in the evidence>",
      "recommended_platform_capability": "<specific platform/process capability>",
      "value_potential": "Very High | High | Medium | Low",
      "value_driver": "Capacity | Cycle Time | Cost | Quality | Risk | Experience",
      "value_hypothesis": "<evidence-grounded statement of how this creates value; do not invent unsupported savings>",
      "priority_score": 0,
      "recommended_priority": "Do Now | Plan | Investigate | Monitor",
      "priority_rationale": "<why this priority is appropriate based on value, effort, confidence, dependencies and urgency>",
      "time_horizon": "0-30 days | 30-60 days | 60-90 days | 90+ days",
      "value_simulation_scenario": "reassignment_churn | rework_loops | waiting_delay | approval_delay | variant_standardisation | top_bottleneck_activity | post_resolution_admin | best_path_shift | automate_repetitive_transition | none",
      "problem_signal": "<the specific evidence — name the transition, metric, or field issue>",
      "how_platform_helps": "<what the named platform capability specifically does>",
      "how_rovo_helps": "<Jira only; for ServiceNow repeat how_platform_helps>",
      "platform_trigger": "<the workflow event that activates this>",
      "rovo_trigger": "<Jira only; for ServiceNow repeat platform_trigger>",
      "platform_config_note": "<one sentence of concrete platform configuration guidance>",
      "rovo_config_note": "<Jira only; for ServiceNow repeat platform_config_note>",
      "expected_benefit": "<outcome in business terms>",
      "owner_hint": "<who should own this>",
      "evidence_points": ["<data point>", "<data point>"]
    }
  ],

  "opportunity_groups": [
    {
      "group_name": "<theme name>",
      "summary": "<1-2 sentences>",
      "linked_opportunities": ["<title>", "<title>"]
    }
  ],

  "implementation_sequence": {
    "phases": [
      {
        "phase": "<phase name>",
        "platform_products_used": ["<product>"],
        "rovo_products_used": ["<Jira only product names; ServiceNow: repeat platform_products_used>"],
        "focus": "<what this phase achieves>",
        "actions": ["<named action from opportunities>", "<named action>"],
        "requires_build": false
      }
    ]
  },

  "target_workflow_state": {
    "summary": "<2-3 sentences — what the workflow looks like after the AI capabilities are in place>",
    "states": [
      {
        "state_name": "",
        "state_type": "Open | Active | Waiting | Review | Closed",
        "purpose": "",
        "entry_rule": "",
        "exit_rule": "",
        "ai_decision_point": false,
        "platform_product": "",
        "platform_action": "",
        "rovo_product": "",
        "rovo_action": ""
      }
    ],
    "preferred_flow": ["<state_name>", "<state_name>"]
  },

  "data_coverage": {
    "process_metrics": true,
    "transitions": true,
    "variants": true,
    "field_usage": true,
    "activity_model": true,
    "time_intelligence": false,
    "sample_items": false,
    "evidence_base_label": ""
  }
}

${reportScopeText}
INPUT EVIDENCE
${inputText}`;
};
